import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../components/Services.css';
import '../components/About.css';

const EngineeringTeamPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const teamInfo = {
    title: 'Engineering & Innovation',
    description: 'Turning ideas into impact. Our engineers and designers build adaptive, engaging games that seniors can enjoy regardless of mobility, cognitive, or sensory challenges. By blending technology, creativity, and accessibility, we create solutions that make play inclusive for all. Our team designs, tests, and refines these games through hands-on collaboration with seniors to ensure they\'re fun, functional, and truly accessible.',
    video: '/videos/7475254-hd_1920_1080_25fps.mp4',
    keyPoints: [
      'Design and develop accessible games for seniors',
      'Collaborate directly with seniors to test and refine solutions',
      'Focus on mobility, cognitive, and sensory accessibility',
      'Blend technology, creativity, and user-centered design',
      'Create inclusive gaming experiences for all abilities'
    ],
    skills: [
      'Game Development',
      'UI/UX Design',
      'Accessibility Testing',
      'User Research',
      'Prototyping',
      'Collaboration'
    ]
  };

  return (
    <div className="page-container">
      <section className="services section" ref={sectionRef}>
        <div className="container">
          <div className={`services-header ${isVisible ? 'animate-fade-in' : ''}`}>
            <h1 className="section-title">{teamInfo.title}</h1>
            <p className="section-subtitle">
              {teamInfo.description}
            </p>
          </div>

          <div className={`team-hero-section ${isVisible ? 'animate-slide-up' : ''}`}>
            <div className="team-hero-video">
              <video 
                src={teamInfo.video} 
                className="team-bg-video"
                autoPlay
                muted
                loop
                playsInline
              />
            </div>
          </div>

          <div className="team-details-grid">
            <div className={`team-details-card ${isVisible ? 'animate-slide-left' : ''}`}>
              <h2>What We Do</h2>
              <ul className="team-key-points">
                {teamInfo.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>

            <div className={`team-details-card ${isVisible ? 'animate-slide-right' : ''}`}>
              <h2>Skills & Focus Areas</h2>
              <div className="skills-grid">
                {teamInfo.skills.map((skill, index) => (
                  <div key={index} className="skill-tag">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Game Projects Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Game Projects</h2>
            <p className="research-section-subtitle">Our accessible games designed for seniors</p>
            <div className="research-placeholder-grid">
              <div className="research-placeholder-card">
                <div className="placeholder-icon">🎮</div>
                <h3>Game Project 1</h3>
                <p className="placeholder-text">[Upload game project details here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="research-placeholder-card">
                <div className="placeholder-icon">🎮</div>
                <h3>Game Project 2</h3>
                <p className="placeholder-text">[Upload game project details here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="research-placeholder-card">
                <div className="placeholder-icon">🎮</div>
                <h3>Game Project 3</h3>
                <p className="placeholder-text">[Upload game project details here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
            </div>
          </div>

          {/* Prototypes & Demos Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Prototypes & Demos</h2>
            <p className="research-section-subtitle">Early stage projects and working prototypes</p>
            <div className="newsletter-placeholder-grid">
              <div className="newsletter-placeholder-card">
                <div className="placeholder-icon">🔧</div>
                <h3>Prototype - [Project Name]</h3>
                <p className="placeholder-text">[Upload prototype details and demo here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="newsletter-placeholder-card">
                <div className="placeholder-icon">🔧</div>
                <h3>Prototype - [Project Name]</h3>
                <p className="placeholder-text">[Upload prototype details and demo here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="newsletter-placeholder-card">
                <div className="placeholder-icon">🔧</div>
                <h3>Prototype - [Project Name]</h3>
                <p className="placeholder-text">[Upload prototype details and demo here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
            </div>
          </div>

          {/* Case Studies Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Case Studies</h2>
            <p className="research-section-subtitle">Documentation of our development process and user testing</p>
            <div className="findings-placeholder-container">
              <div className="finding-placeholder-item">
                <div className="placeholder-icon">📊</div>
                <div className="finding-content">
                  <h3>Case Study 1</h3>
                  <p className="placeholder-text">[Add case study description here - include development process, user feedback, accessibility features, etc.]</p>
                </div>
              </div>
              <div className="finding-placeholder-item">
                <div className="placeholder-icon">📊</div>
                <div className="finding-content">
                  <h3>Case Study 2</h3>
                  <p className="placeholder-text">[Add case study description here - include development process, user feedback, accessibility features, etc.]</p>
                </div>
              </div>
              <div className="finding-placeholder-item">
                <div className="placeholder-icon">📊</div>
                <div className="finding-content">
                  <h3>Case Study 3</h3>
                  <p className="placeholder-text">[Add case study description here - include development process, user feedback, accessibility features, etc.]</p>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Documentation Section */}
          <div className={`research-section ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2 className="research-section-title">Technical Documentation</h2>
            <p className="research-section-subtitle">Technical guides, API documentation, and development resources</p>
            <div className="stories-placeholder-grid">
              <div className="story-placeholder-card">
                <div className="placeholder-icon">📚</div>
                <h3>Technical Guide 1</h3>
                <p className="placeholder-text">[Upload technical documentation here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="story-placeholder-card">
                <div className="placeholder-icon">📚</div>
                <h3>API Documentation</h3>
                <p className="placeholder-text">[Upload API documentation here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
              <div className="story-placeholder-card">
                <div className="placeholder-icon">📚</div>
                <h3>Development Resources</h3>
                <p className="placeholder-text">[Upload development resources here]</p>
                <p className="placeholder-date">[Date]</p>
              </div>
            </div>
          </div>

          <div className={`team-cta ${isVisible ? 'animate-fade-in' : ''}`}>
            <h2>Interested in Joining?</h2>
            <p>We're always looking for passionate engineers, designers, and innovators who want to make a difference.</p>
            <div className="team-cta-buttons">
              <Link to="/get-involved" className="btn btn-primary">
                Get Involved
              </Link>
              <Link to="/teams" className="btn btn-secondary">
                View All Teams
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EngineeringTeamPage;

